/*
  Fireline theme layer — runs on top of Shopify Horizon.
  Horizon keeps ownership of commerce (cart, variants, product forms, filters). This file adds:
    1. Analytics bridge (Shopify Web Pixels + dataLayer + Meta) for the Fireline funnel events.
    2. TRM (COP/USD) widget + USD equivalents. Source: datos.gov.co, dataset 32sa-8pi3
       (Tasa Representativa del Mercado published by the Superintendencia Financiera de Colombia).
       Fallback: manual value from theme settings. Cached in localStorage.
    3. Header behaviour: mega menu hover intent, mobile drawer, sticky state, localization form.
    4. Predictive search (fl-search custom element).
    5. Product page sync: variant changes re-render the Fireline blocks, quantity updates the quote links.
    6. Quote form pre-fill + attribution (UTM, landing page, referrer, product context).
    7. Related products completion through Shopify recommendations.
    8. Cart assist refresh after cart changes.
*/
(() => {
  'use strict';
  const body = document.body;
  const motionOK = document.documentElement.classList.contains('fl-motion');
  const cfg = {
    currency: body.dataset.flCurrency || '',
    trmSource: body.dataset.flTrmSource || 'datos_gov',
    trmFallback: parseFloat(body.dataset.flTrmFallback || '') || 0,
    trmCacheHours: parseInt(body.dataset.flTrmCacheHours || '6', 10) || 6,
    dataLayer: body.dataset.flDatalayer === 'true',
    debug: body.dataset.flDebug === 'true',
    locale: body.dataset.flLocale || 'es',
  };
  window.Fireline = window.Fireline || {};

  /* ------------------------------------------------------------------ */
  /* 1. Analytics                                                        */
  /* ------------------------------------------------------------------ */
  const track = (name, data = {}) => {
    const payload = { ...data, page_path: location.pathname, page_title: document.title };
    try {
      if (window.Shopify?.analytics?.publish) window.Shopify.analytics.publish(`fireline_${name}`, payload);
    } catch (e) { /* pixels sandbox unavailable */ }
    if (cfg.dataLayer) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: name, ...payload });
    }
    if (typeof window.fbq === 'function' && ['quote_submit', 'whatsapp_click', 'quote_click', 'technical_document_download'].includes(name)) {
      try { window.fbq('trackCustom', name, payload); } catch (e) { /* noop */ }
    }
    if (cfg.debug) console.info('[Fireline]', name, payload);
    document.dispatchEvent(new CustomEvent('fireline:event', { detail: { name, payload } }));
  };
  window.Fireline.track = track;

  const productContext = () => {
    if (!body.dataset.flProductId) return {};
    return {
      item_id: body.dataset.flProductId,
      item_name: body.dataset.flProductTitle,
      item_brand: body.dataset.flProductVendor,
      price: parseFloat(body.dataset.flProductPrice || '0') || 0,
      sku: body.dataset.flProductSku || '',
      currency: cfg.currency,
    };
  };

  if (body.dataset.flProductId) track('view_item', productContext());

  document.addEventListener('click', (event) => {
    const el = event.target.closest('[data-fl-event]');
    if (!el) return;
    const name = el.dataset.flEvent;
    const data = { ...productContext() };
    if (el.dataset.flItemId) {
      data.item_id = el.dataset.flItemId;
      data.item_name = el.dataset.flItemName;
      data.item_brand = el.dataset.flItemBrand;
      data.price = parseFloat(el.dataset.flItemPrice || '0') || 0;
    }
    if (el.dataset.flItemName && !el.dataset.flItemId) data.item_name = el.dataset.flItemName;
    if (el.dataset.flDoc) data.document_type = el.dataset.flDoc;
    data.link_url = el.getAttribute('href') || '';
    if (name === 'quote_submit_attempt') return;
    track(name, data);
  });

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (form.matches('form[role="search"]')) {
      const q = form.querySelector('input[name="q"]');
      if (q?.value) track('search', { search_term: q.value.trim() });
    }
    if (form.matches('form[data-type="add-to-cart-form"]')) {
      const qty = form.querySelector('input[name="quantity"]');
      track('add_to_cart', { ...productContext(), quantity: parseInt(qty?.value || '1', 10) || 1 });
    }
  }, true);

  document.addEventListener('click', (event) => {
    const checkout = event.target.closest('button[name="checkout"], a[href*="/checkout"], .cart__checkout-button');
    if (checkout) track('begin_checkout', { value: window.Fireline.cartTotal || undefined, currency: cfg.currency });
  }, true);

  document.addEventListener('cart:update', (event) => {
    const count = event.detail?.data?.itemCount ?? event.detail?.resource?.item_count;
    if (typeof count === 'number') {
      document.querySelectorAll('[data-fl-cart-count]').forEach((el) => {
        el.textContent = count;
        el.classList.toggle('is-empty', count === 0);
        el.classList.remove('is-bump');
        void el.offsetWidth;
        el.classList.add('is-bump');
      });
    }
    refreshCartSections();
  });

  /* ------------------------------------------------------------------ */
  /* 2. TRM + USD equivalents                                            */
  /* ------------------------------------------------------------------ */
  const TRM_KEY = 'fl_trm_v1';
  const TRM_URL = 'https://www.datos.gov.co/resource/32sa-8pi3.json?$order=vigenciadesde%20DESC&$limit=1';
  const usdFormat = (value) => new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', maximumFractionDigits: value >= 100 ? 0 : 2, minimumFractionDigits: value >= 100 ? 0 : 2,
  }).format(value);
  const copFormat = (value) => new Intl.NumberFormat('es-CO', { maximumFractionDigits: 2, minimumFractionDigits: 2 }).format(value);
  const dateFormat = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return new Intl.DateTimeFormat(cfg.locale === 'en' ? 'en-US' : 'es-CO', { day: '2-digit', month: 'short', year: 'numeric' }).format(d);
  };

  const trm = { value: 0, date: '', state: 'loading' };
  window.Fireline.trm = trm;

  const renderTRM = () => {
    document.querySelectorAll('fl-trm').forEach((el) => {
      el.dataset.flTrmState = trm.state;
      const value = el.querySelector('[data-fl-trm-value]');
      const date = el.querySelector('[data-fl-trm-date]');
      const label = el.querySelector('.fl-trm__label');
      if (trm.state === 'unavailable' || !trm.value) {
        if (value) value.textContent = el.dataset.unavailable || 'TRM no disponible';
        if (date) date.textContent = '';
        return;
      }
      if (value) value.textContent = `$${copFormat(trm.value)} COP/USD`;
      if (date) date.textContent = trm.date ? `· ${dateFormat(trm.date)}` : '';
      if (label && trm.state === 'fallback' && el.dataset.reference) label.textContent = el.dataset.reference;
    });
  };

  const convertUSD = (root = document) => {
    if (!trm.value) return;
    root.querySelectorAll('[data-fl-usd]').forEach((el) => {
      const cents = parseFloat(el.dataset.flUsd || '0');
      if (!cents) return;
      const usd = cents / 100 / trm.value;
      const target = el.querySelector('[data-fl-usd-value]') || el;
      target.textContent = usdFormat(usd);
      el.hidden = false;
      el.setAttribute('title', `TRM ${copFormat(trm.value)} COP/USD${trm.date ? ' · ' + dateFormat(trm.date) : ''}`);
    });
  };

  const setTRM = (value, date, state) => {
    trm.value = value; trm.date = date; trm.state = state;
    renderTRM();
    convertUSD();
    document.dispatchEvent(new CustomEvent('fireline:trm', { detail: { ...trm } }));
  };

  const loadTRM = async () => {
    const needsTRM = document.querySelector('fl-trm, [data-fl-usd]');
    if (!needsTRM || cfg.currency !== 'COP') return;
    let cached = null;
    try { cached = JSON.parse(localStorage.getItem(TRM_KEY) || 'null'); } catch (e) { cached = null; }
    const maxAge = cfg.trmCacheHours * 3600 * 1000;
    if (cached?.value && Date.now() - (cached.fetchedAt || 0) < maxAge) {
      setTRM(cached.value, cached.date, 'live');
      return;
    }
    if (cfg.trmFallback) setTRM(cfg.trmFallback, '', 'fallback');
    if (cfg.trmSource === 'manual') { if (!cfg.trmFallback) setTRM(0, '', 'unavailable'); return; }
    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 6000);
      const response = await fetch(TRM_URL, { signal: controller.signal, headers: { Accept: 'application/json' } });
      clearTimeout(timer);
      if (!response.ok) throw new Error(`TRM HTTP ${response.status}`);
      const rows = await response.json();
      const row = Array.isArray(rows) ? rows[0] : null;
      const value = parseFloat(row?.valor);
      if (!value || !Number.isFinite(value)) throw new Error('TRM payload without value');
      const date = row.vigenciadesde || row.vigenciahasta || '';
      try { localStorage.setItem(TRM_KEY, JSON.stringify({ value, date, fetchedAt: Date.now() })); } catch (e) { /* storage blocked */ }
      setTRM(value, date, 'live');
    } catch (error) {
      if (cfg.debug) console.warn('[Fireline] TRM unavailable', error);
      if (cached?.value) setTRM(cached.value, cached.date, 'fallback');
      else if (cfg.trmFallback) setTRM(cfg.trmFallback, '', 'fallback');
      else setTRM(0, '', 'unavailable');
    }
  };

  // Convert nodes injected later (cart re-render, variant re-render, recommendations).
  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      m.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        window.Fireline.observeReveal?.(node);
        if (!trm.value) return;
        if (node.matches?.('[data-fl-usd]')) convertUSD(node.parentNode || document);
        else if (node.querySelector?.('[data-fl-usd]')) convertUSD(node);
      });
    }
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });

  /* ------------------------------------------------------------------ */
  /* 3. Header                                                           */
  /* ------------------------------------------------------------------ */
  const header = document.querySelector('[data-fl-header]');
  if (header) {
    const items = [...header.querySelectorAll('[data-fl-nav-item]')];
    const closeAll = (except) => items.forEach((d) => { if (d !== except && d.open) d.open = false; });
    const hoverable = window.matchMedia('(hover: hover) and (min-width: 1000px)');
    let timer;
    items.forEach((details) => {
      details.addEventListener('toggle', () => { if (details.open) closeAll(details); });
      details.addEventListener('pointerenter', () => {
        if (!hoverable.matches) return;
        clearTimeout(timer);
        timer = setTimeout(() => { details.open = true; }, 90);
      });
      details.addEventListener('pointerleave', () => {
        if (!hoverable.matches) return;
        clearTimeout(timer);
        timer = setTimeout(() => { details.open = false; }, 220);
      });
      const summary = details.querySelector('summary');
      summary?.addEventListener('click', (event) => {
        if (!hoverable.matches) return;
        // On hover devices a click on an open item follows the "see all" link instead of toggling.
        if (details.open) {
          const all = details.querySelector('.fl-mega__all');
          if (all) { event.preventDefault(); location.href = all.href; }
        }
      });
    });
    document.addEventListener('click', (event) => { if (!header.contains(event.target)) closeAll(); });
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      const open = items.find((d) => d.open);
      if (open) { open.open = false; open.querySelector('summary')?.focus(); }
      const drawer = header.querySelector('[data-fl-drawer]');
      if (drawer?.open) { drawer.open = false; drawer.querySelector('summary')?.focus(); }
    });

    const drawer = header.querySelector('[data-fl-drawer]');
    if (drawer) {
      const sync = () => body.classList.toggle('fl-drawer-open', drawer.open);
      drawer.addEventListener('toggle', sync);
      drawer.querySelector('[data-fl-drawer-close]')?.addEventListener('click', () => { drawer.open = false; });
      drawer.querySelector('.fl-drawer__panel')?.addEventListener('click', (event) => {
        if (event.target === event.currentTarget) drawer.open = false;
      });
      window.matchMedia('(min-width: 1000px)').addEventListener('change', (e) => { if (e.matches && drawer.open) drawer.open = false; });
      sync();
    }

    const searchToggle = header.querySelector('[data-fl-search-toggle]');
    const setSearch = (open) => {
      header.classList.toggle('is-search-open', open);
      searchToggle?.setAttribute('aria-expanded', String(open));
      if (open) header.querySelector('.fl-search__input')?.focus();
    };
    searchToggle?.addEventListener('click', () => setSearch(!header.classList.contains('is-search-open')));
    document.addEventListener('keydown', (event) => { if (event.key === 'Escape' && header.classList.contains('is-search-open')) { setSearch(false); searchToggle?.focus(); } });

    // Sticky state + scroll offset for anchors (sticky tabs, #ficha-tecnica…) measured from the real header.
    const utility = header.querySelector('.fl-utility');
    const bar = header.querySelector('.fl-header__bar');
    const measure = () => {
      if (bar) document.documentElement.style.setProperty('--fl-header-offset', `${Math.round(bar.offsetHeight + 16)}px`);
      if (bar) document.documentElement.style.setProperty('--fl-sticky-top', `${Math.round(bar.offsetHeight)}px`);
    };
    measure();
    window.addEventListener('resize', measure, { passive: true });
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        header.classList.toggle('is-stuck', window.scrollY > (utility ? utility.offsetHeight : 0) + 4);
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    header.querySelectorAll('[data-fl-localization-select]').forEach((select) => {
      select.addEventListener('change', () => select.closest('form')?.submit());
    });
  }

  /* ------------------------------------------------------------------ */
  /* 4. Predictive search                                                */
  /* ------------------------------------------------------------------ */
  class FirelineSearch extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('.fl-search__results');
      this.status = this.querySelector('[data-search-status]');
      if (!this.input || !this.results) return;
      this.index = -1;
      this.input.setAttribute('role', 'combobox');
      this.input.setAttribute('aria-autocomplete', 'list');
      this.results.setAttribute('role', 'listbox');
      this.results.setAttribute('aria-label', this.dataset.resultsLabel || 'Productos sugeridos');
      this.input.addEventListener('input', () => {
        clearTimeout(this.timer);
        this.controller?.abort();
        this.close();
        const query = this.input.value.trim();
        if (query.length < 2) return;
        this.timer = setTimeout(() => this.search(query), 220);
      });
      this.input.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') { clearTimeout(this.timer); this.controller?.abort(); this.close(); return; }
        const links = [...this.results.querySelectorAll('a')];
        if (this.results.hidden || !links.length) return;
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          const dir = event.key === 'ArrowDown' ? 1 : -1;
          this.index = this.index < 0 ? (dir > 0 ? 0 : links.length - 1) : (this.index + dir + links.length) % links.length;
          links.forEach((link, i) => link.setAttribute('aria-selected', String(i === this.index)));
          this.input.setAttribute('aria-activedescendant', links[this.index].id);
          links[this.index].scrollIntoView({ block: 'nearest' });
        } else if (event.key === 'Enter' && this.index >= 0) {
          event.preventDefault();
          links[this.index].click();
        }
      });
      this.addEventListener('focusout', () => setTimeout(() => {
        if (!this.contains(document.activeElement)) { clearTimeout(this.timer); this.controller?.abort(); this.close(); }
      }, 0));
    }
    disconnectedCallback() { clearTimeout(this.timer); this.controller?.abort(); }
    close() {
      this.index = -1;
      this.results.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
    }
    async search(query) {
      this.controller = new AbortController();
      try {
        const url = new URL(`${this.dataset.url}.json`, location.origin);
        url.searchParams.set('q', query);
        url.searchParams.set('resources[type]', 'product');
        url.searchParams.set('resources[limit]', '6');
        url.searchParams.set('resources[options][fields]', 'title,vendor,product_type,variants.title,variants.sku');
        const response = await fetch(url, { signal: this.controller.signal });
        if (!response.ok) throw new Error('Search unavailable');
        const data = await response.json();
        if (query !== this.input.value.trim()) return;
        const products = data.resources?.results?.products || [];
        this.results.replaceChildren();
        products.forEach((product, i) => {
          const target = new URL(product.url, location.origin);
          if (target.origin !== location.origin) return;
          const link = document.createElement('a');
          link.href = target.href;
          link.id = `${this.results.id}-option-${i}`;
          link.setAttribute('role', 'option');
          link.setAttribute('aria-selected', 'false');
          link.tabIndex = -1;
          const imageUrl = product.featured_image?.url || product.image;
          if (imageUrl) {
            const image = document.createElement('img');
            image.src = imageUrl; image.alt = ''; image.width = 44; image.height = 44; image.loading = 'lazy';
            link.append(image);
          }
          const label = document.createElement('span');
          const title = document.createElement('strong'); title.textContent = product.title;
          label.append(title);
          if (product.vendor) { const vendor = document.createElement('small'); vendor.textContent = product.vendor; label.append(vendor); }
          link.append(label);
          this.results.append(link);
        });
        const all = document.createElement('a');
        all.href = `${window.Theme?.routes?.search_url || '/search'}?q=${encodeURIComponent(query)}&type=product&options[prefix]=last`;
        all.className = 'fl-search__all';
        all.setAttribute('role', 'option');
        all.setAttribute('aria-selected', 'false');
        all.tabIndex = -1;
        all.id = `${this.results.id}-option-all`;
        all.textContent = `${this.dataset.allLabel || 'Ver todos los resultados'} “${query}”`;
        this.results.append(all);
        if (!products.length) {
          this.status.textContent = this.dataset.hint || 'Sin sugerencias. Pulsa Enter para buscar en el catálogo.';
        } else {
          this.status.textContent = (this.dataset.results || '[n] productos sugeridos.').replace('[n]', products.length).replace('{n}', products.length);
        }
        this.results.hidden = false;
        this.input.setAttribute('aria-expanded', 'true');
      } catch (error) {
        if (error.name !== 'AbortError') this.close();
      }
    }
  }
  if (!customElements.get('fl-search')) customElements.define('fl-search', FirelineSearch);

  /* ------------------------------------------------------------------ */
  /* 5. Product page sync                                                */
  /* ------------------------------------------------------------------ */
  const setParam = (href, key, value) => {
    try { const u = new URL(href, location.origin); u.searchParams.set(key, value); return u.pathname + u.search + u.hash; } catch (e) { return href; }
  };
  const currentQuantity = () => {
    const input = document.querySelector('form[data-type="add-to-cart-form"] input[name="quantity"], product-form-component input[name="quantity"]');
    const qty = parseInt(input?.value || '1', 10);
    return Number.isFinite(qty) && qty > 0 ? qty : 1;
  };
  const syncQuantityLinks = () => {
    const qty = currentQuantity();
    document.querySelectorAll('[data-fl-quote-link]').forEach((link) => { link.setAttribute('href', setParam(link.getAttribute('href'), 'cantidad', qty)); });
    document.querySelectorAll('[data-fl-whatsapp-link]').forEach((link) => {
      if (!link.dataset.flWaBase) link.dataset.flWaBase = link.getAttribute('href');
      try {
        const u = new URL(link.dataset.flWaBase);
        const text = u.searchParams.get('text') || '';
        if (qty > 1) u.searchParams.set('text', `${text}\nCantidad: ${qty}`);
        link.setAttribute('href', u.toString());
      } catch (e) { /* ignore */ }
    });
  };
  document.addEventListener('input', (event) => { if (event.target.matches('input[name="quantity"]')) syncQuantityLinks(); });
  document.addEventListener('change', (event) => { if (event.target.matches('input[name="quantity"]')) syncQuantityLinks(); });
  document.addEventListener('click', (event) => { if (event.target.closest('quantity-selector-component, .quantity-selector')) setTimeout(syncQuantityLinks, 0); });

  document.addEventListener('variant:update', (event) => {
    const detail = event.detail;
    const html = detail?.data?.html;
    if (html?.querySelectorAll) {
      document.querySelectorAll('[data-fl-variant-sync]').forEach((current) => {
        if (detail.data.productId && String(detail.data.productId) !== current.dataset.productId) return;
        const fresh = html.querySelector(`[data-fl-variant-sync="${current.dataset.flVariantSync}"]`);
        if (fresh) current.replaceWith(fresh.cloneNode(true));
      });
      const freshSticky = html.querySelector('.fl-sticky-quote');
      const sticky = document.querySelector('.fl-sticky-quote');
      if (freshSticky && sticky) sticky.setAttribute('href', freshSticky.getAttribute('href'));
    }
    const variant = detail?.resource;
    if (variant) {
      const barcode = document.querySelector('[data-fl-barcode]');
      if (barcode) barcode.textContent = variant.barcode || '—';
      const sku = document.querySelector('[data-fl-sku]');
      if (sku) sku.textContent = variant.sku || '—';
    }
    document.querySelectorAll('[data-fl-whatsapp-link]').forEach((link) => { delete link.dataset.flWaBase; });
    syncQuantityLinks();
    convertUSD();
  });

  /* ------------------------------------------------------------------ */
  /* 6. Quote form pre-fill + attribution                                */
  /* ------------------------------------------------------------------ */
  const ATTR_KEY = 'fl_attr_v1';
  const params = new URLSearchParams(location.search);
  const readAttr = () => { try { return JSON.parse(sessionStorage.getItem(ATTR_KEY) || '{}'); } catch (e) { return {}; } };
  const attribution = readAttr();
  let attrChanged = false;
  ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach((key) => {
    if (params.get(key) && attribution[key] !== params.get(key)) { attribution[key] = params.get(key); attrChanged = true; }
  });
  if (!attribution.landing_page) { attribution.landing_page = location.href.split('#')[0]; attrChanged = true; }
  if (!attribution.referrer && document.referrer && !document.referrer.startsWith(location.origin)) { attribution.referrer = document.referrer; attrChanged = true; }
  if (attrChanged) { try { sessionStorage.setItem(ATTR_KEY, JSON.stringify(attribution)); } catch (e) { /* noop */ } }

  // Every quote form on the page (quote page, home request form) is pre-filled the same way.
  document.querySelectorAll('[data-fl-quote-form]').forEach((quoteForm) => {
    const producto = params.get('producto') || '';
    const variante = params.get('variante') || '';
    const sku = params.get('sku') || params.get('mpn') || '';
    const cantidad = params.get('cantidad') || '';
    const productField = quoteForm.querySelector('[data-fl-contact-product]');
    const skuField = quoteForm.querySelector('[data-fl-contact-sku]');
    const qtyField = quoteForm.querySelector('[data-fl-contact-quantity]');
    if (productField && producto && !productField.value) productField.value = variante ? `${producto} — ${variante}` : producto;
    if (skuField && sku && !skuField.value) skuField.value = sku;
    if (qtyField && cantidad && !qtyField.value) qtyField.value = cantidad;
    const body = quoteForm.querySelector('textarea[name="contact[body]"]');
    const carrito = params.get('carrito') || '';
    if (body && !body.value) {
      const lines = [];
      if (producto) lines.push(`Producto: ${producto}${variante ? ' — ' + variante : ''}`);
      if (sku) lines.push(`Referencia: ${sku}`);
      if (cantidad) lines.push(`Cantidad: ${cantidad}`);
      if (carrito) lines.push(carrito);
      if (lines.length) body.value = `${lines.join('\n')}\n\n`;
    }
    quoteForm.querySelectorAll('[data-fl-attr]').forEach((input) => {
      const key = input.dataset.flAttr;
      input.value = params.get(key) || attribution[key] || '';
    });
    const banner = quoteForm.closest('.fl-quote')?.querySelector('[data-fl-quote-prefilled]');
    if (banner && producto) {
      banner.textContent = (banner.dataset.template || 'Solicitud para: [product]').replace('[product]', sku ? `${producto} (${sku})` : producto).replace('{product}', sku ? `${producto} (${sku})` : producto);
      banner.hidden = false;
    }
    const success = quoteForm.querySelector('[data-fl-contact-success]');
    if (success) {
      success.focus({ preventScroll: false });
      track('quote_submit', { form_id: quoteForm.id, product: producto, sku });
    }
  });

  /* ------------------------------------------------------------------ */
  /* 7. Related products completion                                      */
  /* ------------------------------------------------------------------ */
  // Order: curated (metafields) › Shopify recommendations › same collection › same brand › same type.
  // The server renders curated + fallback candidates (extras hidden); recommendations are slotted in
  // after the curated cards and everything beyond the limit stays hidden.
  document.querySelectorAll('[data-fl-related-fetch]').forEach((relatedFetch) => {
    const section = relatedFetch.closest('[data-fl-related]');
    const grid = section?.querySelector('[data-fl-related-grid]');
    if (!section || !grid) return;
    const limit = parseInt(grid.dataset.limit || '4', 10);
    const run = async () => {
      let recs = [];
      try {
        const response = await fetch(relatedFetch.dataset.url, { headers: { Accept: 'text/html' } });
        if (response.ok) {
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          recs = [...doc.querySelectorAll('[data-fl-related-grid] [data-fl-tier="rec"]')];
        }
      } catch (e) { if (cfg.debug) console.warn('[Fireline] recommendations unavailable', e); }
      if (!recs.length) return;
      const cards = [...grid.children];
      const seen = new Set();
      if (body.dataset.flProductId) seen.add(body.dataset.flProductId);
      const ordered = [];
      const take = (card) => {
        const id = card.dataset.productId;
        if (!id || seen.has(id)) { card.remove(); return; }
        seen.add(id);
        ordered.push(card);
      };
      cards.filter((c) => c.dataset.flTier === 'curated').forEach(take);
      recs.forEach((card) => take(document.importNode(card, true)));
      cards.filter((c) => c.dataset.flTier !== 'curated').forEach(take);
      ordered.forEach((card, i) => {
        card.hidden = i >= limit;
        card.style.setProperty('--fl-i', String(i % limit));
        grid.append(card);
      });
      if (ordered.length) { section.hidden = false; convertUSD(section); }
    };
    if ('requestIdleCallback' in window) requestIdleCallback(run, { timeout: 2500 }); else setTimeout(run, 600);
  });

  /* ------------------------------------------------------------------ */
  /* 8. Cart assist refresh + misc                                        */
  /* ------------------------------------------------------------------ */
  let refreshing = false;
  async function refreshCartSections() {
    const targets = [...document.querySelectorAll('[data-fl-refresh]')];
    if (!targets.length || refreshing) return;
    refreshing = true;
    try {
      for (const el of targets) {
        const id = el.dataset.flRefresh;
        const response = await fetch(`${location.pathname}?section_id=${encodeURIComponent(id)}`, { headers: { Accept: 'text/html' } });
        if (!response.ok) continue;
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const fresh = doc.querySelector('[data-fl-refresh]');
        if (fresh) el.replaceWith(fresh); else el.remove();
      }
      convertUSD();
    } catch (e) { /* keep stale block */ } finally { refreshing = false; }
  }

  document.addEventListener('click', async (event) => {
    const copy = event.target.closest('[data-fl-copy]');
    if (!copy) return;
    try {
      await navigator.clipboard.writeText(copy.dataset.flCopy);
      copy.classList.add('is-copied');
      setTimeout(() => copy.classList.remove('is-copied'), 1600);
    } catch (e) { /* clipboard blocked */ }
  });

  // Technical sheet: scroll spy with a sliding indicator; the active tab stays visible on mobile.
  const techNav = document.querySelector('[data-fl-scrollspy]');
  if (techNav && 'IntersectionObserver' in window) {
    const track = techNav.querySelector('.fl-technical__nav-track');
    const ink = techNav.querySelector('.fl-technical__ink');
    const links = [...techNav.querySelectorAll('.fl-technical__nav-track a[href^="#"]')];
    const targets = links.map((a) => document.getElementById(a.getAttribute('href').slice(1))).filter(Boolean);
    const setActive = (id) => {
      let active = null;
      links.forEach((a) => {
        const on = a.getAttribute('href') === `#${id}`;
        a.classList.toggle('is-active', on);
        if (on) { a.setAttribute('aria-current', 'true'); active = a; } else a.removeAttribute('aria-current');
      });
      if (active && ink) { ink.style.width = `${active.offsetWidth}px`; ink.style.setProperty('--ink-x', `${active.offsetLeft}px`); }
      if (active && track && track.scrollWidth > track.clientWidth) track.scrollTo({ left: active.offsetLeft - 24, behavior: motionOK ? 'smooth' : 'auto' });
    };
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => { if (entry.isIntersecting) setActive(entry.target.id); });
    }, { rootMargin: '-35% 0px -55% 0px' });
    targets.forEach((t) => io.observe(t));
    if (targets[0]) setActive(targets[0].id);
  }

  // Cart total for begin_checkout value (Horizon renders it in the summary).
  const totalNode = document.querySelector('[data-cart-subtotal]');
  if (totalNode) {
    const digits = (totalNode.getAttribute('value') || '').replace(/[^\d,.-]/g, '').replace(/\./g, '').replace(',', '.');
    const total = parseFloat(digits);
    if (Number.isFinite(total)) window.Fireline.cartTotal = total;
  }


  /* ------------------------------------------------------------------ */
  /* 9. V3 interactions: reveal, tabs, hero, counters                    */
  /* ------------------------------------------------------------------ */

  // Scroll reveal. Styles only exist under html.fl-motion, so content is visible without JS.
  let revealIO = null;
  const observeReveal = (scope = document) => {
    if (!revealIO) return;
    const nodes = scope.matches?.('[data-fl-reveal]') ? [scope] : [];
    scope.querySelectorAll?.('[data-fl-reveal]:not(.is-in)').forEach((el) => nodes.push(el));
    nodes.forEach((el) => revealIO.observe(el));
  };
  if (motionOK) {
    revealIO = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        revealIO.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -6% 0px', threshold: 0.06 });
    observeReveal(document);
    window.addEventListener('beforeprint', () => document.querySelectorAll('[data-fl-reveal]').forEach((el) => el.classList.add('is-in')));
    document.addEventListener('shopify:section:load', (event) => observeReveal(event.target));
  }
  window.Fireline.observeReveal = observeReveal;

  // Real-number counters (value already in the HTML; animation is cosmetic).
  const counters = [...document.querySelectorAll('[data-fl-count]')];
  if (motionOK && counters.length) {
    const countIO = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        countIO.unobserve(entry.target);
        const el = entry.target;
        const target = parseInt(el.dataset.flCount, 10);
        if (!Number.isFinite(target) || target < 10) return;
        const start = performance.now();
        const duration = 1100;
        const step = (now) => {
          const p = Math.min(1, (now - start) / duration);
          el.textContent = String(Math.round(target * (1 - Math.pow(1 - p, 3))));
          if (p < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      });
    }, { threshold: 0.6 });
    counters.forEach((el) => countIO.observe(el));
  }

  // Accessible tabs (explorer, product sheet, blog…).
  document.querySelectorAll('[data-fl-tabs]').forEach((tabs) => {
    const list = tabs.querySelector('[role="tablist"]');
    if (!list) return;
    const buttons = [...list.querySelectorAll('[role="tab"]')];
    const ink = list.querySelector('.fl-tabs__ink');
    const moveInk = () => {
      const active = buttons.find((b) => b.getAttribute('aria-selected') === 'true');
      if (!ink || !active || !active.offsetWidth) return;
      list.classList.add('has-ink');
      ink.style.width = `${active.offsetWidth}px`;
      ink.style.setProperty('--ink-x', `${active.offsetLeft}px`);
    };
    const select = (button, focus) => {
      buttons.forEach((b) => {
        const on = b === button;
        b.setAttribute('aria-selected', String(on));
        b.tabIndex = on ? 0 : -1;
        document.getElementById(b.getAttribute('aria-controls'))?.classList.toggle('is-active', on);
      });
      if (focus) button.focus();
      button.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      moveInk();
      track('discovery_tab', { tab: button.textContent.trim() });
    };
    buttons.forEach((b) => b.addEventListener('click', () => select(b)));
    list.addEventListener('keydown', (event) => {
      const i = buttons.indexOf(document.activeElement);
      if (i < 0) return;
      const map = { ArrowRight: i + 1, ArrowLeft: i - 1, Home: 0, End: buttons.length - 1 };
      if (!(event.key in map)) return;
      event.preventDefault();
      select(buttons[(map[event.key] + buttons.length) % buttons.length], true);
    });
    moveInk();
    window.addEventListener('resize', moveInk, { passive: true });
    document.fonts?.ready.then(moveInk);
  });

  // Solutions index: hovering / focusing a sector swaps the preview (image or large icon).
  document.querySelectorAll('[data-fl-sectors]').forEach((list) => {
    const section = list.closest('.fl-solutions');
    const title = section?.querySelector('[data-fl-preview-title]');
    const index = section?.querySelector('[data-fl-preview-index-label]');
    const activate = (link) => {
      const i = link.dataset.flSector;
      list.querySelectorAll('.fl-sector').forEach((li) => li.classList.toggle('is-active', li.contains(link)));
      section.querySelectorAll('[data-fl-preview-image]').forEach((img) => img.classList.toggle('is-active', img.dataset.flPreviewImage === i));
      section.querySelectorAll('[data-fl-preview-icon]').forEach((ic) => ic.classList.toggle('is-active', ic.dataset.flPreviewIcon === i));
      section.classList.toggle('has-image', !!section.querySelector(`[data-fl-preview-image="${i}"]`));
      if (title) title.textContent = link.dataset.flSectorTitle || '';
      if (index) index.textContent = String(Number(i) + 1).padStart(2, '0');
    };
    list.querySelectorAll('[data-fl-sector]').forEach((link) => {
      link.addEventListener('pointerenter', () => activate(link));
      link.addEventListener('focus', () => activate(link));
    });
    const first = list.querySelector('[data-fl-sector]');
    if (first) activate(first);
  });

  // Client-side filter (brands directory): search + category chips.
  document.querySelectorAll('[data-fl-filter]').forEach((scope) => {
    const input = scope.querySelector('[data-fl-filter-input]');
    const chips = [...scope.querySelectorAll('[data-fl-filter-chip]')];
    const items = [...scope.querySelectorAll('[data-fl-filter-list] > [data-name]')];
    const count = scope.querySelector('[data-fl-filter-count]');
    const empty = scope.querySelector('[data-fl-filter-empty]');
    let category = '';
    const normalize = (value) => value.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
    const apply = () => {
      const q = normalize(input?.value || '');
      let shown = 0;
      items.forEach((item) => {
        const ok = (!q || normalize(item.dataset.name || '').includes(q)) && (!category || item.dataset.category === category);
        item.classList.toggle('is-filtered', !ok);
        if (ok) { shown += 1; item.classList.add('is-in'); }
      });
      if (count) count.textContent = `${shown} / ${items.length}`;
      if (empty) empty.hidden = shown > 0;
    };
    input?.addEventListener('input', apply);
    chips.forEach((chip) => chip.addEventListener('click', () => {
      category = chip.dataset.flFilterChip || '';
      chips.forEach((c) => { const on = c === chip; c.classList.toggle('is-active', on); c.setAttribute('aria-pressed', String(on)); });
      apply();
    }));
    apply();
  });

  // Toast (quick add feedback, comparator limits).
  const toast = (html, timeout = 3200) => {
    document.querySelector('.fl-toast')?.remove();
    const el = document.createElement('div');
    el.className = 'fl-toast';
    el.setAttribute('role', 'status');
    el.innerHTML = html;
    document.body.append(el);
    setTimeout(() => el.remove(), timeout);
  };
  const escapeHTML = (value) => String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const shopRoot = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
  const labelsNode = document.querySelector('[data-fl-compare-labels]');
  let uiLabels = {};
  try { uiLabels = JSON.parse(labelsNode?.textContent || '{}'); } catch (e) { uiLabels = {}; }

  // Quick add from cards: native form POST to /cart/add, enhanced with fetch + toast.
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest?.('form[data-fl-quick-add]');
    if (!form || !window.fetch) return;
    event.preventDefault();
    const button = form.querySelector('button[type="submit"]');
    button?.classList.add('is-loading');
    try {
      const response = await fetch(`${shopRoot}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.description || data?.message || '');
      const cart = await fetch(`${shopRoot}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: { resource: cart, sourceId: 'fl-quick-add', data: { itemCount: cart.item_count, source: 'fl-quick-add' } } }));
      track('add_to_cart', { item_id: button?.dataset.flItemId, item_name: button?.dataset.flItemName, item_brand: button?.dataset.flItemBrand, price: parseFloat(button?.dataset.flItemPrice || '0') || 0, quantity: 1, currency: cfg.currency });
      button?.classList.add('is-added');
      setTimeout(() => button?.classList.remove('is-added'), 1800);
      toast(`<svg class="fl-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m5 12 5 5L20 7"/></svg><span>${escapeHTML(uiLabels.added || 'Añadido al carrito')}</span><a href="${shopRoot}cart">${escapeHTML(uiLabels.view_cart || 'Ver carrito')}</a>`);
    } catch (error) {
      toast(`<span>${escapeHTML(error.message || uiLabels.error || 'No fue posible añadir el producto.')}</span>`);
    } finally {
      button?.classList.remove('is-loading');
    }
  });

  // Product comparator (tray + dialog). Data comes from sections/fl-compare-item.liquid.
  class FirelineCompare extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.key = 'fl_compare_v1';
      this.max = parseInt(this.dataset.max || '4', 10);
      try { this.items = JSON.parse(localStorage.getItem(this.key) || '[]'); } catch (e) { this.items = []; }
      if (!Array.isArray(this.items)) this.items = [];
      this.list = this.querySelector('[data-fl-compare-items]');
      this.dialog = this.querySelector('[data-fl-compare-dialog]');
      this.body = this.querySelector('[data-fl-compare-body]');
      document.addEventListener('click', (event) => {
        const toggle = event.target.closest('[data-fl-compare-toggle]');
        if (!toggle) return;
        event.preventDefault();
        event.stopPropagation();
        this.toggle({ handle: toggle.dataset.handle, title: toggle.dataset.title, image: toggle.dataset.image });
      });
      this.querySelector('[data-fl-compare-open]')?.addEventListener('click', () => this.open());
      this.querySelector('[data-fl-compare-clear]')?.addEventListener('click', () => { this.items = []; this.save(); });
      this.querySelector('[data-fl-compare-close]')?.addEventListener('click', () => this.dialog?.close());
      this.dialog?.addEventListener('click', (event) => { if (event.target === this.dialog) this.dialog.close(); });
      this.list?.addEventListener('click', (event) => {
        const remove = event.target.closest('[data-remove]');
        if (!remove) return;
        this.items = this.items.filter((i) => i.handle !== remove.dataset.remove);
        this.save();
      });
      this.render();
    }
    save() {
      try { localStorage.setItem(this.key, JSON.stringify(this.items)); } catch (e) { /* storage blocked */ }
      this.render();
    }
    toggle(item) {
      if (!item.handle) return;
      const exists = this.items.some((i) => i.handle === item.handle);
      if (exists) this.items = this.items.filter((i) => i.handle !== item.handle);
      else if (this.items.length >= this.max) { toast(`<span>${escapeHTML(this.dataset.full || 'Máximo 4 productos')}</span>`); return; }
      else { this.items.push(item); track('compare_add', { item_name: item.title }); }
      this.save();
    }
    render() {
      this.hidden = this.items.length === 0;
      this.querySelectorAll('[data-fl-compare-count]').forEach((el) => { el.textContent = this.items.length; });
      if (this.list) {
        this.list.innerHTML = this.items.map((i) => `<li>${i.image ? `<img src="${escapeHTML(i.image)}" alt="${escapeHTML(i.title)}" width="48" height="48" loading="lazy">` : `<span class="fl-kicker">${escapeHTML(i.title).slice(0, 12)}</span>`}<button type="button" data-remove="${escapeHTML(i.handle)}" aria-label="${escapeHTML(uiLabels.remove || 'Quitar')}: ${escapeHTML(i.title)}">×</button></li>`).join('');
      }
      const handles = new Set(this.items.map((i) => i.handle));
      document.querySelectorAll('[data-fl-compare-toggle]').forEach((t) => t.setAttribute('aria-pressed', String(handles.has(t.dataset.handle))));
    }
    async open() {
      if (!this.dialog || !this.items.length) return;
      if (typeof this.dialog.showModal === 'function') this.dialog.showModal(); else this.dialog.setAttribute('open', '');
      this.body.innerHTML = `<p class="fl-compare__loading">${escapeHTML(uiLabels.loading || '…')}</p>`;
      track('compare_open', { items: this.items.length });
      const products = (await Promise.all(this.items.map(async (item) => {
        try {
          const response = await fetch(`${shopRoot}products/${encodeURIComponent(item.handle)}?section_id=fl-compare-item`);
          if (!response.ok) return null;
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          return JSON.parse(doc.querySelector('[data-fl-compare-data]')?.textContent || 'null');
        } catch (e) { return null; }
      }))).filter(Boolean);
      const rows = [
        ['brand', (p) => p.vendor], ['sku', (p) => p.sku], ['mpn', (p) => p.mpn], ['price', (p) => p.price],
        ['availability', (p) => p.availability], ['type', (p) => p.type],
      ];
      const specNames = [];
      products.forEach((p) => (p.specs || []).forEach(([name]) => { if (!specNames.includes(name)) specNames.push(name); }));
      specNames.forEach((name) => rows.push([name, (p) => (p.specs || []).find(([n]) => n === name)?.[1], true]));
      rows.push(['applications', (p) => p.applications], ['compatibility', (p) => p.compatibility], ['certifications', (p) => p.certifications], ['warranty', (p) => p.warranty]);
      const head = `<thead><tr><th scope="col"></th>${products.map((p) => `<td><a class="fl-compare__product" href="${escapeHTML(p.url)}">${p.image ? `<img src="${escapeHTML(p.image)}" alt="" loading="lazy">` : ''}<span>${escapeHTML(p.title)}</span></a></td>`).join('')}</tr></thead>`;
      const body = rows.map(([key, get, raw]) => {
        const values = products.map((p) => (get(p) ?? '').toString().trim());
        if (!values.some(Boolean)) return '';
        const diff = new Set(values).size > 1;
        const label = raw ? key : (uiLabels[key] || key);
        return `<tr class="${diff ? 'is-diff' : ''}"><th scope="row">${escapeHTML(label)}</th>${values.map((v) => `<td>${v ? escapeHTML(v) : '—'}</td>`).join('')}</tr>`;
      }).join('');
      this.body.innerHTML = `<table class="fl-compare__table">${head}<tbody>${body}</tbody></table>`;
    }
  }
  if (!customElements.get('fl-compare')) customElements.define('fl-compare', FirelineCompare);

  // Article reading progress.
  const progress = document.querySelector('[data-fl-progress]');
  const articleEl = document.querySelector('.fl-article__content');
  if (progress && articleEl) {
    let frame = 0;
    const update = () => {
      const rect = articleEl.getBoundingClientRect();
      const total = rect.height - window.innerHeight * 0.6;
      const value = Math.min(1, Math.max(0, (-rect.top + window.innerHeight * 0.2) / (total || 1)));
      progress.style.setProperty('--fl-progress', value.toFixed(4));
      frame = 0;
    };
    window.addEventListener('scroll', () => { if (!frame) frame = requestAnimationFrame(update); }, { passive: true });
    update();
  }

  // Hero: subtle pointer parallax (desktop, fine pointer, motion allowed).
  const hero = document.querySelector('[data-fl-hero][data-fl-parallax]');
  if (hero && motionOK && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
    let frame = 0;
    hero.addEventListener('pointermove', (event) => {
      const rect = hero.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      const y = ((event.clientY - rect.top) / rect.height) * 2 - 1;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        hero.style.setProperty('--px', x.toFixed(3));
        hero.style.setProperty('--py', y.toFixed(3));
      });
    });
    hero.addEventListener('pointerleave', () => { hero.style.setProperty('--px', '0'); hero.style.setProperty('--py', '0'); });
  }

  // Product carousel: scroll-snap track + arrows, dots, mouse drag, optional autoplay with pause.
  // "Más vendidos" carousels replace their slides with /collections/<handle>?sort_by=best-selling.
  class FirelineCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-fl-track]');
      this.controls = this.querySelector('[data-fl-controls]');
      this.dots = this.querySelector('[data-fl-dots]');
      this.playBtn = this.querySelector('[data-fl-play]');
      this.limit = parseInt(this.dataset.limit || '12', 10);
      this.querySelector('[data-fl-prev]')?.addEventListener('click', () => this.go(-1));
      this.querySelector('[data-fl-next]')?.addEventListener('click', () => this.go(1));
      this.track.addEventListener('scroll', () => this.onScroll(), { passive: true });
      this.track.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); this.go(1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(-1); }
      });
      this.bindDrag();
      this.bindAutoplay();
      window.addEventListener('resize', () => this.layout(), { passive: true });
      this.layout();
      if (this.dataset.src) this.loadSource();
    }
    get slides() { return [...this.track.querySelectorAll(':scope > [data-fl-slide]')]; }
    get perView() {
      const first = this.slides[0];
      if (!first) return 1;
      return Math.max(1, Math.round(this.track.clientWidth / first.getBoundingClientRect().width));
    }
    get pages() { return Math.max(1, Math.ceil(this.slides.length / this.perView)); }
    layout() {
      const scrollable = this.track.scrollWidth > this.track.clientWidth + 4;
      if (this.controls) this.controls.hidden = !scrollable;
      if (this.dots) {
        const pages = this.pages;
        if (this.dots.children.length !== pages) {
          this.dots.innerHTML = '';
          for (let i = 0; i < pages; i += 1) {
            const dot = document.createElement('button');
            dot.type = 'button';
            dot.className = 'fl-carousel__dot';
            dot.setAttribute('aria-label', `${i + 1} / ${pages}`);
            dot.addEventListener('click', () => this.toPage(i));
            this.dots.append(dot);
          }
        }
        this.dots.hidden = pages < 2;
      }
      this.onScroll();
    }
    toPage(i) {
      const slide = this.slides[i * this.perView];
      if (!slide) return;
      this.track.scrollTo({ left: slide.offsetLeft - this.slides[0].offsetLeft, behavior: motionOK ? 'smooth' : 'auto' });
    }
    go(dir) {
      const max = this.track.scrollWidth - this.track.clientWidth - 2;
      if (dir > 0 && this.track.scrollLeft >= max) { this.track.scrollTo({ left: 0, behavior: motionOK ? 'smooth' : 'auto' }); return; }
      if (dir < 0 && this.track.scrollLeft <= 2) { this.track.scrollTo({ left: max, behavior: motionOK ? 'smooth' : 'auto' }); return; }
      this.track.scrollBy({ left: dir * this.track.clientWidth, behavior: motionOK ? 'smooth' : 'auto' });
    }
    onScroll() {
      if (!this.dots) return;
      const width = this.slides[0]?.getBoundingClientRect().width || 1;
      const page = Math.min(this.pages - 1, Math.round(this.track.scrollLeft / (width * this.perView)));
      [...this.dots.children].forEach((dot, i) => dot.setAttribute('aria-current', i === page ? 'true' : 'false'));
    }
    bindDrag() {
      if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
      let startX = 0; let startLeft = 0; let dragging = false; let moved = false;
      this.track.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        dragging = true; moved = false; startX = e.clientX; startLeft = this.track.scrollLeft;
      });
      window.addEventListener('pointermove', (e) => {
        if (!dragging) return;
        const dx = e.clientX - startX;
        if (!moved && Math.abs(dx) > 6) { moved = true; this.classList.add('is-dragging'); }
        if (moved) this.track.scrollLeft = startLeft - dx;
      });
      window.addEventListener('pointerup', () => {
        if (!dragging) return;
        dragging = false;
        if (moved) {
          this.classList.remove('is-dragging');
          const width = this.slides[0]?.getBoundingClientRect().width || 1;
          this.track.scrollTo({ left: Math.round(this.track.scrollLeft / width) * width, behavior: motionOK ? 'smooth' : 'auto' });
        }
      });
      this.track.addEventListener('click', (e) => { if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; } }, true);
      this.track.addEventListener('dragstart', (e) => e.preventDefault());
    }
    bindAutoplay() {
      const delay = parseInt(this.dataset.autoplay || '0', 10);
      if (!delay || !motionOK) { if (this.playBtn) this.playBtn.hidden = true; return; }
      this.paused = false;
      this.hold = false;
      const tick = () => { if (!this.paused && !this.hold && !document.hidden) this.go(1); };
      this.timer = setInterval(tick, delay);
      const hold = (value) => () => { this.hold = value; };
      this.addEventListener('pointerenter', hold(true));
      this.addEventListener('pointerleave', hold(false));
      this.addEventListener('focusin', hold(true));
      this.addEventListener('focusout', hold(false));
      this.playBtn?.addEventListener('click', () => {
        this.paused = !this.paused;
        this.playBtn.setAttribute('aria-pressed', String(this.paused));
        this.playBtn.setAttribute('aria-label', this.paused ? this.playBtn.dataset.labelPlay : this.playBtn.dataset.labelPause);
        this.classList.toggle('is-paused', this.paused);
      });
    }
    async loadSource() {
      try {
        const response = await fetch(this.dataset.src, { headers: { Accept: 'text/html' } });
        if (!response.ok) return;
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const items = [...doc.querySelectorAll('[data-fl-rail-items] [data-fl-slide]')].slice(0, this.limit);
        if (!items.length) return;
        this.track.replaceChildren(...items.map((n) => document.importNode(n, true)));
        const section = this.closest('[data-fl-await-source]');
        if (section) section.hidden = false;
        this.track.scrollLeft = 0;
        convertUSD(this);
        this.layout();
      } catch (e) { if (cfg.debug) console.warn('[Fireline] best sellers unavailable', e); }
    }
  }
  if (!customElements.get('fl-carousel')) customElements.define('fl-carousel', FirelineCarousel);


  // Rich text tables (product descriptions, articles, pages) scroll inside their own box on small screens.
  document.querySelectorAll('.rte table').forEach((table) => {
    if (table.parentElement?.classList.contains('fl-table-scroll')) return;
    const wrap = document.createElement('div');
    wrap.className = 'fl-table-scroll';
    table.before(wrap);
    wrap.append(table);
  });

  loadTRM();
  syncQuantityLinks();
})();
